import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiamond } from '@fortawesome/free-solid-svg-icons';
import { FiArrowRight, FiCheck, FiChevronLeft, FiChevronRight, FiLock, FiUsers } from 'react-icons/fi';
import { SiApple, SiGoogle, SiStripe, SiTesla } from 'react-icons/si';
import { formatPostedAge } from './opportunityData';
import JobMatchesUpgrade from './JobMatchesUpgrade';
import { useLanguage } from '../contexts/LanguageContext';
import './opportunities.css';

export function TrialDiamond({ trial }) {
  return trial ? <FontAwesomeIcon icon={faDiamond} className="opp-diamond" title="Included in your trial" /> : null;
}

export function TrialIndicator({ access }) {
  const { language } = useLanguage();
  if (!access.trial) return null;
  const days = Math.max(1, Math.ceil((access.endsAt - Date.now()) / 86400000));
  if (language === 'ar') {
    const remaining = days === 1 ? 'يوم واحد متبقٍ' : days === 2 ? 'يومان متبقيان' : `${new Intl.NumberFormat('ar').format(days)} أيام متبقية`;
    return <span className="opp-trial"><TrialDiamond trial /> التجربة مفعّلة <span>· {remaining}</span></span>;
  }
  return <span className="opp-trial"><TrialDiamond trial /> Trial active <span>· {days} {days === 1 ? 'day' : 'days'} left</span></span>;
}

export function CompanyMark({ name = '', small = false }) {
  const key = name.toLowerCase().replace(/\W/g, '');
  const Icon = key.startsWith('apple') ? SiApple : key.startsWith('google') ? SiGoogle : key.startsWith('stripe') ? SiStripe : key.startsWith('tesla') ? SiTesla : null;
  return <span className={`opp-company-mark ${small ? 'is-small' : ''} is-${key}`} aria-hidden="true">{Icon ? <Icon /> : name.slice(0, 1).toUpperCase()}</span>;
}

export function JobTabs({ access, value, onChange, applied = true, user, profile }) {
  const { language } = useLanguage();
  const ar = language === 'ar';
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  useEffect(() => { if (access.premium) setUpgradeOpen(false); }, [access.premium]);
  return <><div className="opp-tabs" role="tablist" aria-label={ar ? 'عرض الوظائف' : 'Job views'}>
    <button type="button" role="tab" aria-label={ar ? 'وظائف تناسبك' : 'Job Matches'} aria-selected={value === 'matches'} title={!access.premium ? (ar ? 'اكتشف وظائف تناسبك مع Pro' : 'Unlock Job Matches with Pro') : undefined}
      aria-haspopup={!access.premium ? 'dialog' : undefined} aria-expanded={!access.premium ? upgradeOpen : undefined}
      onClick={() => access.premium ? onChange('matches') : setUpgradeOpen(true)}><span className="js-desktop-only">{ar ? 'وظائف تناسبك' : 'Job Matches'}</span><span className="js-mobile-only" aria-hidden="true">{ar ? 'تناسبك' : 'Matches'}</span> {access.premium ? <TrialDiamond trial={access.trial} /> : <FiLock aria-label={ar ? 'مقفل' : 'Locked'} />}</button>
    <button type="button" role="tab" aria-label={ar ? 'البحث في كل الوظائف' : 'Search All Jobs'} aria-selected={value === 'browse'} onClick={() => onChange('browse')}><span className="js-desktop-only">{ar ? 'البحث في كل الوظائف' : 'Search All Jobs'}</span><span className="js-mobile-only" aria-hidden="true">{ar ? 'ابحث' : 'Search'}</span></button>
    {applied && <button type="button" role="tab" aria-selected={value === 'applied'} onClick={() => onChange('applied')}>{ar ? 'قدّمت لها' : 'Applied'}</button>}
  </div>{upgradeOpen && !access.premium && <JobMatchesUpgrade user={user} profile={profile} access={access} onClose={() => setUpgradeOpen(false)} />}</>;
}

export function MatchBreakdown({ job, compact = false }) {
  const score = Number.isFinite(job.fitScore) ? Math.min(100, Math.max(0, Math.round(job.fitScore))) : null;
  return <div className={`opp-match-breakdown ${compact ? 'is-compact' : ''}`}>
    <div className="opp-score" style={{ '--score': `${(score || 0) * 3.6}deg` }} aria-label={score == null ? 'Match score pending' : `${score}% match`}>
      <div><strong>{score == null ? '—' : `${score}%`}</strong><span>Match</span></div>
    </div>
    <div className="opp-match-reasons">
      {job.reason && <ul><li><FiCheck /><span><strong>Why this role was matched</strong><small>{job.reason}</small></span></li></ul>}
      {job.concern && <div className="opp-skill"><span className="opp-skill-icon">!</span><span><strong>Worth considering</strong><small>{job.concern}</small></span></div>}
    </div>
  </div>;
}

export function JobListCard({ job, active, onClick, showMatch = false, badge }) {
  return <button type="button" className={`opp-job-row ${active ? 'is-active' : ''}`} onClick={() => onClick(job)} aria-current={active ? 'true' : undefined}>
    <CompanyMark name={job.companyName} small />
    <span className="opp-job-row-copy"><strong>{job.jobOpening || job.title || job.jobTitle}</strong><span>{job.companyName}</span><small>{[job.companyLocation || job.location, job.workspace || job.remoteType].filter(Boolean).join(' · ')}</small><small>{formatPostedAge(job)}{showMatch && job.fitScore != null ? ` · ${Math.round(job.fitScore)}% match` : ''}</small></span>
    {badge && <span className="opp-job-badge">{badge}</span>}
    <FiChevronRight className="opp-job-row-arrow" />
  </button>;
}

export function MatchCarousel({ jobs, onJobClick }) {
  const [index, setIndex] = useState(0);
  const safeIndex = Math.min(index, Math.max(0, jobs.length - 1));
  const job = jobs[safeIndex];
  if (!job) return <div className="opp-empty"><FiUsers /><h2>Your next opportunity is on its way</h2><p>We’re looking for roles that fit your goals. You can explore your job feed in the meantime.</p></div>;
  const skills = Array.isArray(job.skills) ? job.skills : [];
  return <article className="opp-match-card">
    <div className="opp-match-card-main">
      <div className="opp-card-pagination"><span>{safeIndex + 1} <span>/ {jobs.length} Job Matches</span></span><div><button type="button" aria-label="Previous job match" disabled={safeIndex === 0} onClick={() => setIndex(safeIndex - 1)}><FiChevronLeft /></button><button type="button" aria-label="Next job match" disabled={safeIndex === jobs.length - 1} onClick={() => setIndex(safeIndex + 1)}><FiChevronRight /></button></div></div>
      <h2><button type="button" onClick={() => onJobClick(job)}>{job.jobOpening}</button></h2>
      <p className="opp-muted opp-job-meta"><CompanyMark name={job.companyName} small /><span>{[job.companyName, job.companyLocation, job.schedule || job.workspace].filter(Boolean).join(' · ')}</span></p>
      {skills.length > 0 && <div className="opp-tags">{skills.slice(0, 4).map((skill) => <span key={skill}>{skill}</span>)}{skills.length > 4 && <span>+{skills.length - 4}</span>}</div>}
      <button type="button" className="opp-button opp-view-job" onClick={() => onJobClick(job)}>View job <FiArrowRight /></button>
    </div>
    <MatchBreakdown job={job} compact />
  </article>;
}

export { default as NetworkingSuggestions } from './NetworkingSuggestions';
